import React, { useEffect, useState } from 'react';
import { HashRouter, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';

import {
    ensureCsrfCookie,
    isUnauthorizedError,
    loadCurrentUser,
    loadDashboardData,
    loginUser,
    logoutUser,
} from './api';
import ReservePage from './ReservePage';
import SearchPage from './SearchPage';
import SearchDetailPage from './SearchDetailPage';
import EngineerStatisticsPage from './EngineerStatisticsPage';
import MotivationPage from './MotivationPage';
import AcceptancePage from './AcceptancePage';
import AssemblingPage from './AssemblingPage';
import DashboardPage from './DashboardPage';
import ProfilePage from './ProfilePage';
import CassettesPage from './CassettesPage';
import ModuleBatchesPage from './ModuleBatchesPage';
import WaitingActsPage from './WaitingActsPage';
import WaitingShipmentsPage from './WaitingShipmentsPage';
import CassetteBatchDetailPage from './CassetteBatchDetailPage';
import CassetteGuaranteeDetailPage from './CassetteGuaranteeDetailPage';
import ReserveDetailPage from './ReserveDetailPage';
import WaitingActsDetailPage from './WaitingActsDetailPage';
import WaitingShipmentsDetailPage from './WaitingShipmentsDetailPage';
import { fallbackMenuConfig } from './menuConfig';
import './designTokens.css';
import './AppShell.css';


const fallbackData = {
    user: {
        full_name: 'Оператор Hyosung',
        location: { city: 'Тюмень' },
        permissions: [
            'hyosung.custom_packaging',
            'hyosung.custom_logist',
        ],
        menu: fallbackMenuConfig,
    },
    dashboard: {
        filters: {
            start_date: '2026-05-01',
            end_date: '2026-05-21',
            location: 'Тюмень',
            location_choices: [
                { value: '', label: 'Все' },
                { value: 'Тюмень', label: 'Тюмень' },
            ],
        },
        module_cards: [
            { system: '2X', title: 'SYSTEM 2X', total_bad: 91, total_good: 150, total_assembling: 0, total_ready_to_ship: 4 },
            { system: 'CIT 2X', title: 'SYSTEM CIT 2X', total_bad: 54, total_good: 37, total_assembling: 0, total_ready_to_ship: 0 },
            { system: 'TE 2X', title: 'SYSTEM TE 2X', total_bad: 78, total_good: 124, total_assembling: 0, total_ready_to_ship: 0 },
            { system: '5X', title: 'SYSTEM 5X', total_bad: 463, total_good: 91, total_assembling: 0, total_ready_to_ship: 16 },
            { system: 'CIT 5X', title: 'SYSTEM CIT 5X', total_bad: 25, total_good: 45, total_assembling: 0, total_ready_to_ship: 0 },
        ],
        chart: {
            items: [
                { system: '2X', done: 80, plan: 0 },
                { system: 'CIT 2X', done: 116, plan: 0 },
                { system: 'TE 2X', done: 22, plan: 0 },
                { system: '5X', done: 178, plan: 0 },
                { system: 'CIT 5X', done: 77, plan: 0 },
            ],
        },
        daily_report: [],
    },
};

function LoginPage({ loading, error, warning, onSubmit }) {
    const [username, setUsername] = useState('admin');
    const [password, setPassword] = useState('');

    async function handleSubmit(event) {
        event.preventDefault();
        await onSubmit({ username, password });
    }

    return (
        <div className="auth-shell">
            <section className="auth-card auth-card--intro">
                <span className="auth-eyebrow">Hyosung</span>
                <h1 className="auth-title">Вход в систему</h1>
                <p className="auth-copy">Локальный контур управления ремонтом и заявками.</p>
            </section>

            <section className="auth-card auth-card--form">
                <div className="auth-formHeader">
                    <span className="auth-eyebrow">Вход</span>
                    <h2>Авторизация</h2>
                    <p>Используй рабочий логин и пароль.</p>
                </div>

                <form className="auth-form" onSubmit={handleSubmit}>
                    <label className="auth-field">
                        <span>Логин</span>
                        <input
                            type="text"
                            value={username}
                            onChange={(event) => setUsername(event.target.value)}
                            autoComplete="username"
                            disabled={loading}
                        />
                    </label>

                    <label className="auth-field">
                        <span>Пароль</span>
                        <input
                            type="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            autoComplete="current-password"
                            disabled={loading}
                        />
                    </label>

                    {warning ? <div className="auth-message auth-message--warning">{warning}</div> : null}
                    {error ? <div className="auth-message auth-message--error">{error}</div> : null}

                    <button type="submit" className="auth-button" disabled={loading}>
                        {loading ? 'Проверяем доступ...' : 'Войти в Hyosung'}
                    </button>
                </form>
            </section>
        </div>
    );
}


function UserAvatar({ user, className }) {
    const [broken, setBroken] = useState(false);
    const label = user?.full_name || user?.username || 'Пользователь';
    const initial = label.slice(0, 1).toUpperCase();

    useEffect(() => {
        setBroken(false);
    }, [user?.profile_picture_url]);

    if (user?.profile_picture_url && !broken) {
        return (
            <img
                className={className}
                src={user.profile_picture_url}
                alt={label}
                onError={() => setBroken(true)}
            />
        );
    }

    return (
        <div className={`${className} ${className}--placeholder`}>
            {initial}
        </div>
    );
}


function SidebarItem({ item }) {
    const location = useLocation();
    const hasChildren = Boolean(item.children?.length);
    const childActive = hasChildren && item.children.some((child) => location.pathname.startsWith(child.route));
    const [open, setOpen] = useState(childActive || item.id === 'engineers');

    useEffect(() => {
        if (childActive) {
            setOpen(true);
        }
    }, [childActive]);

    if (hasChildren) {
        return (
            <li className="shell-navItem shell-navItem--withChildren">
                <button
                    type="button"
                    className={`shell-navToggle${childActive ? ' is-active' : ''}`}
                    onClick={() => setOpen((current) => !current)}
                    aria-expanded={open}
                >
                    <span>{item.label}</span>
                    <span className={`shell-navCaret${open ? ' is-open' : ''}`} aria-hidden="true" />
                </button>
                {open ? (
                    <ul className="shell-subnav">
                        {item.children.map((child) => (
                            <li key={child.id}>
                                <NavLink
                                    to={child.route}
                                    className={({ isActive }) => `shell-subnavLink${isActive ? ' is-active' : ''}`}
                                >
                                    {child.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                ) : null}
            </li>
        );
    }

    return (
        <li className="shell-navItem">
            <NavLink to={item.route} className={({ isActive }) => `shell-navLink${isActive ? ' is-active' : ''}`}>
                <span>{item.label}</span>
            </NavLink>
        </li>
    );
}


function ShellLayout({ data, warning, onLogout, onProfileUpdated }) {
    const location = useLocation();
    const menu = data.user.menu?.length ? data.user.menu : fallbackMenuConfig;
    const locationLabel = data.user.location?.city || 'Локация не определена';
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.classList.toggle('shell-body--menuOpen', sidebarOpen);

        return () => {
            document.body.classList.remove('shell-body--menuOpen');
        };
    }, [sidebarOpen]);

    return (
        <div className={`shell-layout${sidebarOpen ? ' is-mobileMenuOpen' : ''}`}>
            <button
                type="button"
                className="shell-sidebarOverlay"
                aria-hidden={!sidebarOpen}
                tabIndex={sidebarOpen ? 0 : -1}
                onClick={() => setSidebarOpen(false)}
            />

            <aside className={`shell-sidebar${sidebarOpen ? ' is-mobileOpen' : ''}`}>
                <div className="shell-mobileSidebarTop">
                    <div className="shell-brandBlock">
                        <h1 className="shell-brand">Hyosung</h1>
                    </div>
                    <button
                        type="button"
                        className="shell-sidebarCloseButton"
                        onClick={() => setSidebarOpen(false)}
                        aria-label="Закрыть меню"
                    >
                        ×
                    </button>
                </div>

                <div className="shell-sidebarPanel">
                    <span className="shell-sidebarLabel">Рабочая зона</span>
                    <strong className="shell-sidebarValue">{locationLabel}</strong>
                </div>

                <nav className="shell-nav">
                    <div className="shell-navTitle">Разделы</div>
                    <ul className="shell-navList">
                        {menu.map((item) => (
                            <SidebarItem key={item.id} item={item} />
                        ))}
                    </ul>
                </nav>
            </aside>

            <main className="shell-main">
                <header className="shell-header shell-header--minimal">
                    <button
                        type="button"
                        className="shell-menuButton"
                        onClick={() => setSidebarOpen((current) => !current)}
                        aria-label={sidebarOpen ? 'Закрыть меню' : 'Открыть меню'}
                    >
                        <span />
                        <span />
                        <span />
                    </button>

                    <div className="shell-headerAside">
                        <div className="shell-userBadge">
                            <UserAvatar user={data.user} className="shell-userAvatar" />
                            <span>
                                {data.user.full_name}
                                {data.user.location?.city ? `, ${data.user.location.city}` : ''}
                            </span>
                        </div>
                        <div className="shell-headerActions">
                            <NavLink to="/app/profile" className="shell-profileLink">
                                Профиль
                            </NavLink>
                            <button type="button" className="shell-logoutButton" onClick={onLogout}>
                                Выйти
                            </button>
                        </div>
                    </div>
                </header>

                {warning ? <div className="shell-banner">{warning}</div> : null}

                <Routes>
                    <Route path="/" element={<Navigate to="/app" replace />} />
                    <Route path="/app" element={<DashboardPage initialDashboard={data.dashboard} />} />
                    <Route path="/app/profile" element={<ProfilePage user={data.user} onProfileUpdated={onProfileUpdated} />} />
                    <Route path="/app/search" element={<SearchPage />} />
                    <Route path="/app/search/:moduleId" element={<SearchDetailPage />} />
                    <Route
                        path="/app/acceptance"
                        element={<AcceptancePage />}
                    />
                    <Route
                        path="/app/requests/assemble"
                        element={<AssemblingPage />}
                    />
                    <Route
                        path="/app/module-batches"
                        element={<ModuleBatchesPage />}
                    />
                    <Route
                        path="/app/cassettes"
                        element={<CassettesPage />}
                    />
                    <Route
                        path="/app/cassettes/batches/:territoryId/:arrivalDate/:numberKits"
                        element={<CassetteBatchDetailPage />}
                    />
                    <Route
                        path="/app/cassettes/guarantee/:serialNumber"
                        element={<CassetteGuaranteeDetailPage />}
                    />
                    <Route
                        path="/app/reserve"
                        element={<ReservePage />}
                    />
                    <Route
                        path="/app/reserve/:slug"
                        element={<ReserveDetailPage />}
                    />
                    <Route
                        path="/app/acts/waiting"
                        element={<WaitingActsPage />}
                    />
                    <Route
                        path="/app/acts/waiting/:slug"
                        element={<WaitingActsDetailPage />}
                    />
                    <Route
                        path="/app/shipments/waiting"
                        element={<WaitingShipmentsPage />}
                    />
                    <Route
                        path="/app/shipments/waiting/:slug"
                        element={<WaitingShipmentsDetailPage />}
                    />
                    <Route
                        path="/app/engineers/statistics"
                        element={<EngineerStatisticsPage />}
                    />
                    <Route
                        path="/app/engineers/motivation"
                        element={<MotivationPage />}
                    />
                    <Route path="*" element={<Navigate to="/app" replace />} />
                </Routes>
            </main>
        </div>
    );
}


export default function App() {
    const [state, setState] = useState({
        data: fallbackData,
        authStatus: 'checking',
        loading: true,
        warning: '',
        authWarning: '',
        loginError: '',
        loginSubmitting: false,
    });

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            setState((current) => ({
                ...current,
                loading: true,
                loginError: '',
            }));

            try {
                await ensureCsrfCookie();
            } catch (_error) {
                // CSRF cookie will be retried on login. No-op for read bootstrap.
            }

            let currentUser;
            try {
                currentUser = await loadCurrentUser();
            } catch (error) {
                if (!cancelled) {
                    if (isUnauthorizedError(error)) {
                        setState((current) => ({
                            ...current,
                            authStatus: 'unauthenticated',
                            loading: false,
                            warning: '',
                            authWarning: '',
                            loginSubmitting: false,
                        }));
                    } else {
                        setState((current) => ({
                            ...current,
                            authStatus: 'unauthenticated',
                            loading: false,
                            warning: '',
                            authWarning: 'Backend не ответил. Проверь локальный сервер и попробуй снова.',
                            loginSubmitting: false,
                        }));
                    }
                }
                return;
            }

            try {
                const data = await loadDashboardData();
                if (!cancelled) {
                    setState({
                        data,
                        authStatus: 'authenticated',
                        loading: false,
                        warning: '',
                        authWarning: '',
                        loginError: '',
                        loginSubmitting: false,
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    if (isUnauthorizedError(error)) {
                        setState((current) => ({
                            ...current,
                            authStatus: 'unauthenticated',
                            loading: false,
                            warning: '',
                            authWarning: '',
                            loginSubmitting: false,
                        }));
                    } else {
                        setState({
                            data: {
                                ...fallbackData,
                                user: {
                                    ...fallbackData.user,
                                    ...currentUser,
                                    menu: currentUser.menu?.length ? currentUser.menu : fallbackData.user.menu,
                                    permissions: currentUser.permissions?.length ? currentUser.permissions : fallbackData.user.permissions,
                                },
                            },
                            authStatus: 'authenticated',
                            loading: false,
                            warning: 'Часть данных недоступна. Показан локальный пример.',
                            authWarning: '',
                            loginError: '',
                            loginSubmitting: false,
                        });
                    }
                }
            }
        }

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, []);

    async function handleLogin(credentials) {
        setState((current) => ({
            ...current,
            loginSubmitting: true,
            loginError: '',
            authWarning: '',
        }));

        try {
            await ensureCsrfCookie();
            await loginUser(credentials);

            setState((current) => ({
                ...current,
                authStatus: 'checking',
            }));

            const data = await loadDashboardData();
            setState({
                data,
                authStatus: 'authenticated',
                loading: false,
                warning: '',
                authWarning: '',
                loginError: '',
                loginSubmitting: false,
            });
        } catch (error) {
            setState((current) => ({
                ...current,
                authStatus: 'unauthenticated',
                loading: false,
                loginSubmitting: false,
                loginError: error?.response?.data?.detail || 'Не удалось выполнить вход. Проверь логин и пароль.',
            }));
        }
    }

    async function handleLogout() {
        try {
            await logoutUser();
        } catch (_error) {
            // Even if the backend session is already gone, frontend should return to login.
        }

        try {
            await ensureCsrfCookie();
        } catch (_error) {
            // Ignore: next login attempt will retry.
        }

        setState({
            data: fallbackData,
            authStatus: 'unauthenticated',
            loading: false,
            warning: '',
            authWarning: '',
            loginError: '',
            loginSubmitting: false,
        });
    }

    function handleProfileUpdated(nextUser) {
        setState((current) => ({
            ...current,
            data: {
                ...current.data,
                user: {
                    ...current.data.user,
                    ...nextUser,
                },
            },
        }));
    }

    return (
        <HashRouter>
            {state.authStatus === 'authenticated' ? (
                <ShellLayout
                    data={state.data}
                    warning={state.warning}
                    onLogout={handleLogout}
                    onProfileUpdated={handleProfileUpdated}
                />
            ) : (
                <LoginPage
                    loading={state.loading || state.loginSubmitting}
                    error={state.loginError}
                    warning={state.authWarning}
                    onSubmit={handleLogin}
                />
            )}
        </HashRouter>
    );
}
